<template>
  <div
    class="q-pa-sm text-subtitle1 text-bold"
    :class="$q.dark.mode ? '' : 'text-primary'"
  >
    {{ symbol }} : Holding Distribution of Mutual Fund
  </div>

  <q-card class="full-height">
    <q-table
      :class="
        $q.dark.mode
          ? 'table-sticky-header-column-dark'
          : 'table-sticky-header-column'
      "
      v-if="mutualfundData"
      :rows="mutualfundData"
      :columns="dynamicColumns"
      :pagination="pagination"
      separator="cell"
    >
      <template v-slot:body-cell-stock_name="props">
        <q-td :props="props">
          <div class="col">
            {{ props.value }}
            <q-tooltip>{{
              allStocks.find((stock) => stock.symbol == props.value)?.name
            }}</q-tooltip>
          </div>
        </q-td>
      </template>
    </q-table>
  </q-card>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";
import useMutualFund from "src/use/mutualfund";
import { useQuasar } from "quasar";
import useUtils from "src/use/utils";

const props = defineProps({
  symbol: {
    type: String,
  },
});

const $q = useQuasar();
const store = useStore();
const { StockWiseMutualFund } = useMutualFund();
const { months } = useUtils();

const allStocks = ref([]);
const mutualfundData = ref([]);

const loadData = async (symbol) => {
  const symbolId = allStocks.value.find((i) => i.symbol === symbol)?.id;

  if (symbolId) {
    $q.loading.show({});
    try {
      const [stockWiseMutualFundRes] = await Promise.all([
        StockWiseMutualFund(symbolId),
      ]);

      const symbolIdMap = allStocks.value.reduce((map, item) => {
        map[item.id] = item.symbol;
        return map;
      }, {});

      const stockWiseMutualFundData = stockWiseMutualFundRes.data
        .map((item) => {
          const fiscalMonth = item.fiscal_month;
          const monthName = months[fiscalMonth] || "Unknown";
          const symbol_name = symbolIdMap[item.symbol] || null;
          const stock_name = symbolIdMap[item.stock] || null;
          const fiscal_month_quantity = monthName + " - " + item.quantity;

          return {
            ...item,
            fiscal_month_name: monthName,
            symbol_name,
            stock_name,
            fiscal_month_quantity,
          };
        })
        .sort((a, b) => {
          // Compare by fiscal_year first
          if (a.fiscal_year !== b.fiscal_year) {
            return a.fiscal_year - b.fiscal_year;
          }
          // If fiscal_year is the same, compare by fiscal_month
          return a.fiscal_month - b.fiscal_month;
        });

      mutualfundData.value = createStockWiseMutualFundData(
        stockWiseMutualFundData.reverse()
      );
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      $q.loading.hide();
    }
  }
};

// Required data generated
const createStockWiseMutualFundData = (data) => {
  const result = data.reduce((result, current) => {
    const { stock_name, fiscal_year, fiscal_month_name, quantity } = current;

    const existingItem = result.find((item) => item.stock_name === stock_name);

    if (existingItem) {
      // existingItem[fiscal_month_name] = quantity;
      existingItem[`${fiscal_year}-${fiscal_month_name}`] = quantity;
    } else {
      const newItem = { stock_name };
      // newItem[fiscal_month_name] = quantity;
      newItem[`${fiscal_year}-${fiscal_month_name}`] = quantity;
      result.push(newItem);
    }

    return result;
  }, []);

  return result;
};

watch(
  () => props.symbol,
  (newVal, oldVal) => {
    loadData(newVal);
  }
);

onMounted(async () => {
  allStocks.value = store.state.core.allStocks || [];
  if (!allStocks.value.length) {
    await store.dispatch("core/getAllStocks");
    allStocks.value = store.state.core.allStocks;
  }

  loadData(props.symbol);
});

// Dynamic Columns Created for Table
const collectMonthsFromData = (data) => {
  const months = new Set();

  data.forEach((row) => {
    for (const key in row) {
      if (key !== "stock_name" && key.includes("-")) {
        months.add(key);
      }
    }
  });

  return Array.from(months);
};

const createDynamicColumns = () => {
  const columns = [
    {
      name: "stock_name",
      required: true,
      label: "Stock Name",
      align: "left",
      field: "stock_name",
    },
  ];

  const monthsInData = collectMonthsFromData(mutualfundData.value);

  monthsInData.forEach((month) => {
    columns.push({
      name: month,
      required: true,
      label: month,
      align: "center",
      field: month,
    });
  });

  return columns;
};

const dynamicColumns = computed(createDynamicColumns);

const pagination = ref({
  rowsPerPage: 15,
  // sortBy: "quantity",
  // descending: true,
});
</script>
